/**
 * Auth store — Zustand mirror of Flutter `AuthController`.
 * Holds the current user + login/logout actions; pages read this hook to
 * gate routes and render the user's name/avatar.
 */
import { create } from 'zustand';
import { authApi, pickAccessToken, pickRefreshToken, type SessionUser } from '@/api/auth';
import { tokenStorage } from '@/api/tokenStorage';

type User = SessionUser;

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  /** Bootstrap from any persisted JWT — call once at app start. */
  bootstrap: () => Promise<void>;
  sendCode: (phoneNumber: string) => Promise<void>;
  verifyCode: (phoneNumber: string, code: string) => Promise<User>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  initialized: false,
  async bootstrap() {
    if (!tokenStorage.getAccess()) {
      set({ initialized: true });
      return;
    }
    try {
      const user = await authApi.me();
      set({ user, initialized: true });
    } catch {
      tokenStorage.clear();
      set({ user: null, initialized: true });
    }
  },
  async sendCode(phoneNumber) {
    set({ loading: true });
    try {
      await authApi.sendVerificationCode(phoneNumber);
    } finally {
      set({ loading: false });
    }
  },
  async verifyCode(phoneNumber, code) {
    set({ loading: true });
    try {
      const res = await authApi.verifyCode(phoneNumber, code);
      const access = pickAccessToken(res);
      const refresh = pickRefreshToken(res);
      if (!access || !refresh) {
        throw new Error('Tokens missing in verify response');
      }
      tokenStorage.set(access, refresh);
      set({ user: res.user });
      return res.user;
    } finally {
      set({ loading: false });
    }
  },
  async logout() {
    await authApi.logout();
    tokenStorage.clear();
    set({ user: null });
  },
}));
